import { useEffect, useState } from 'react'

/**
 * Recherche B2B en cours : étape réelle envoyée par le worker (analyse de la demande, base Sirene,
 * coordonnées), compteur des entreprises traitées et dernières entreprises trouvées.
 */
export function SearchingAnimation({ query, progress, found = [] }) {
  const [dots, setDots] = useState('')

  useEffect(() => {
    const dotsTimer = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'))
    }, 500)
    return () => clearInterval(dotsTimer)
  }, [])

  const total = progress?.total || 0
  const current = progress?.current || 0
  const percent = total ? Math.round((current / total) * 100) : null
  const step = total
    ? `Recherche des coordonnées · ${current}/${total} entreprise${total > 1 ? 's' : ''}`
    : (progress?.message || 'Démarrage de la recherche')
  const latest = found.slice(-4).reverse()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px', gap: 20 }}>
      <div style={{ position: 'relative', width: 72, height: 72 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: '3px solid rgb(var(--rgb-brand) / 0.1)',
          borderTop: '3px solid rgb(var(--rgb-brand))',
          animation: 'spin 1s linear infinite',
        }} />
        <div style={{
          position: 'absolute', inset: 10, borderRadius: '50%',
          border: '2px solid rgb(var(--rgb-brand) / 0.05)',
          borderBottom: '2px solid rgb(var(--rgb-brand) / 0.4)',
          animation: 'spin 1.5s linear infinite reverse',
        }} />
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: percent === null ? 24 : 15, fontWeight: 600, color: 'var(--color-text)',
        }}>
          {percent === null ? '🔍' : `${percent}%`}
        </div>
      </div>

      {query && (
        <div style={{ fontSize: 12, color: 'rgb(var(--rgb-ink) / 0.3)', fontStyle: 'italic', maxWidth: 320, textAlign: 'center' }}>
          « {query} »
        </div>
      )}

      <div style={{ fontSize: 14, color: 'rgb(var(--rgb-ink) / 0.65)', minHeight: 22, textAlign: 'center' }}>
        {step}{dots}
      </div>

      {total > 0 && (
        <div style={{ width: 280, height: 6, borderRadius: 3, background: 'rgb(var(--rgb-ink) / 0.08)', overflow: 'hidden' }}>
          <div style={{ width: `${percent}%`, height: '100%', background: 'rgb(var(--rgb-brand))', transition: 'width 0.4s ease' }} />
        </div>
      )}

      {latest.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 320, maxWidth: '100%' }}>
          {latest.map((p) => (
            <div
              key={p.contactId}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, color: 'rgb(var(--rgb-ink) / 0.55)' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✓ {p.companyName}</span>
              <span style={{ flexShrink: 0, color: 'rgb(var(--rgb-ink) / 0.35)' }}>{p.ville}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function LoadingAnimation({ targetNumber, query, onComplete }) {
  const [counter, setCounter] = useState(0)
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('Analyse en cours...')

  const messages = [
    'Analyse en cours...',
    'Recherche des profils...',
    'Calcul de pertinence...',
    'Finalisation...',
  ]

  useEffect(() => {
    // Animation duration based on target number (max 25s)
    const baseDuration = 2000
    const durationPerThousand = 200
    const randomFactor = 0.85 + Math.random() * 0.3
    const rawDuration = (baseDuration + (targetNumber / 1000) * durationPerThousand) * randomFactor
    const duration = Math.min(rawDuration, 25000)

    // Counter animation
    const counterSteps = Math.min(100, Math.max(50, Math.floor(duration / 40)))
    const counterIncrement = targetNumber / counterSteps
    const counterInterval = duration / counterSteps

    let currentCounter = 0
    const counterTimer = setInterval(() => {
      currentCounter += counterIncrement
      if (currentCounter >= targetNumber) {
        currentCounter = targetNumber
        clearInterval(counterTimer)
      }
      setCounter(Math.floor(currentCounter))
    }, counterInterval)

    // Progress bar animation
    const progressSteps = 100
    const progressInterval = duration / progressSteps

    let currentProgress = 0
    const progressTimer = setInterval(() => {
      currentProgress += 1
      setProgress(currentProgress)

      // Change message proportionally
      const newIndex = Math.floor(currentProgress / (100 / messages.length))
      if (newIndex < messages.length) {
        setMessage(messages[newIndex])
      }

      if (currentProgress >= 100) {
        clearInterval(progressTimer)
        setMessage('✓ Terminé !')
        setTimeout(() => {
          onComplete?.()
        }, 300)
      }
    }, progressInterval)

    return () => {
      clearInterval(counterTimer)
      clearInterval(progressTimer)
    }
  }, [targetNumber, onComplete])

  return (
    <div className="preview-inline-container">
      <div className="preview-counter-section">
        <div className="preview-counter-label">Prospects disponibles</div>
        <div className="preview-counter-number" style={{ transform: counter === targetNumber ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.2s' }}>
          {counter.toLocaleString()}
        </div>
        <div className="preview-query">"{query || 'Recherche...'}"</div>

        <div className="preview-loading-bar">
          <div className="preview-loading-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="preview-loading-text" style={{ color: progress === 100 ? 'rgb(var(--rgb-brand))' : 'inherit' }}>
          {message}
        </div>
      </div>
    </div>
  )
}
